import React, { useState, useCallback } from 'react';
import { formatPhone } from '../../helpers/phoneUtils';
import { CardContent, Grid, IconButton, Button, Divider } from '@material-ui/core';
import moment from 'moment';
import LiabilityAgreementForm from './LiabilityAgreementForm';
import { EditIcon, EnvelopeIcon, SmsIcon } from '../../icons';
import { useModals, MODAL_TYPE } from '../../hooks/useModal';
import { useCapability } from '../../hooks/useCapabilities';
import { CAPABILITIES } from '../../capabilities/capabilities';
import { useNotify } from '../../hooks/useNotify';
import { doSwaggerCall } from '../../hooks/useSwaggerClient';

// Alias for backward compatibility
const Email = EnvelopeIcon;
const Sms = SmsIcon;

export default function GuestPageSidebar({
    guest,
    openEditModal,
    isAgent,
    updateGuest,
    reloadGuestData,
    guestId
}) {
    const [showNotes, setShowNotes] = useState(false);
    const { showModal } = useModals();
    const { hasCapability } = useCapability();
    const { notifySuccess, notifyError } = useNotify();

    const handlePwreset = useCallback(
        async (email, clientId) => {
            try {
                await doSwaggerCall('auth', 'resetPasswordRequest', {
                    data: {
                        email,
                        clientId
                    }
                });
                notifySuccess('Password reset email sent, please check your inbox.');
            } catch (error) {
                notifyError(
                    error?.response?.body?.message || 'Failed to send password reset email'
                );
            }
        },
        [notifySuccess, notifyError]
    );

    return (
        <CardContent>
            <Grid container alignItems="center">
                <Grid item className="mr-auto">
                    <h2>{guest?.role === 'agent' ? 'Agent' : 'Guest'} profile</h2>
                </Grid>
                <Grid item>
                    <IconButton onClick={openEditModal}>
                        <EditIcon />
                    </IconButton>
                </Grid>
            </Grid>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <h5>Full name</h5>
                    <h4>
                        {guest?.firstname} {guest?.lastname}
                    </h4>
                </Grid>
                <Grid item xs={12}>
                    <h5>Email</h5>
                    <h4>{guest?.email}</h4>
                </Grid>
                <Grid item xs={12}>
                    <h5>Phone</h5>
                    <h4>{formatPhone(guest?.phone)}</h4>
                </Grid>
                {guest?.arrival_date !== null && (
                    <Grid item xs={12}>
                        <h5>Arrival date</h5>
                        <h4>{moment(guest?.arrival_date).format('ddd. MMM DD, YYYY')}</h4>
                    </Grid>
                )}
                {guest?.departure_date !== null && (
                    <Grid item xs={12}>
                        <h5>Departure date</h5>
                        <h4>{moment(guest?.departure_date).format('ddd. MMM DD, YYYY')}</h4>
                    </Grid>
                )}
                {guest?.guest_field_info?.map(field => {
                    if (
                        field.value === '' ||
                        field.value === null ||
                        typeof field.value === 'undefined'
                    ) {
                        return null;
                    }
                    let displayValue;

                    if (typeof field.value === 'boolean') {
                        displayValue = field.value.toString();
                    } else if (field.hotel?.name && typeof field.value === 'number') {
                        // Hotel type field
                        displayValue = field.hotel.name;
                    } else if (field.value?.type === 'lodging') {
                        // Lodging type field - display name property
                        displayValue = field.value.name || '';
                    } else {
                        displayValue = field.value;
                    }
                    return (
                        <Grid item xs={12} key={`field-${field.id}`}>
                            <h5>{field.name}</h5>
                            <h4>{displayValue}</h4>
                        </Grid>
                    );
                })}
                <Grid item xs={12}>
                    <h5>Notifications</h5>
                    <h4>
                        {guest?.notification?.length === 0
                            ? 'no notifications'
                            : guest?.notification?.join(', ')}
                    </h4>
                </Grid>
                <Grid item xs={12}>
                    <h5>Additional email addresses</h5>
                    {guest?.additional_emails?.length > 0 ? (
                        guest.additional_emails.map((email, idx) => (
                            <h4 key={idx} style={{ marginBottom: '10px' }}>
                                {email}
                            </h4>
                        ))
                    ) : (
                        <h4 style={{ marginBottom: '10px' }}>-</h4>
                    )}
                </Grid>
                {isAgent && false && (
                    <LiabilityAgreementForm
                        guest={guest}
                        updateGuest={updateGuest}
                        reloadGuestData={reloadGuestData}
                    />
                )}
                {isAgent && (
                    <Grid container item>
                        <Button onClick={() => setShowNotes(!showNotes)} size="small">
                            Show internal notes
                        </Button>
                    </Grid>
                )}
                <Divider style={{ width: '100%', margin: '10px 0' }} />
                {hasCapability(CAPABILITIES.SEND_CUSTOM_MESSAGES) && guestId !== 'me' && (
                    <Grid container item spacing={1}>
                        <Grid container item>
                            <Button
                                variant="contained"
                                disabled={!guest?.phone}
                                onClick={() =>
                                    showModal(MODAL_TYPE.SEND_SMS, {
                                        userId: guest.id,
                                        phoneNumber: guest.phone
                                    })
                                }
                                size="small"
                                color="primary"
                                fullWidth
                                startIcon={<Sms />}
                            >
                                Send Text Message
                            </Button>
                        </Grid>
                        <Grid container item>
                            <Button
                                variant="contained"
                                disabled={!guest?.email && guest?.additional_emails?.length === 0}
                                onClick={() => {
                                    showModal(MODAL_TYPE.SEND_EMAIL, {
                                        userId: guest.id,
                                        emails: [
                                            ...new Set([guest?.email, ...guest?.additional_emails])
                                        ]
                                    });
                                }}
                                size="small"
                                color="primary"
                                fullWidth
                                startIcon={<Email />}
                            >
                                Send Email
                            </Button>
                        </Grid>
                        <Grid container item>
                            <Button
                                variant="contained"
                                disabled={!guest?.email}
                                onClick={() => {
                                    showModal(MODAL_TYPE.CONFIRMATION, {
                                        title: 'Send password reset email',
                                        content: (
                                            <>
                                                Are you sure you want to send a password reset email
                                                to{' '}
                                                <strong>
                                                    {guest?.firstname} {guest?.lastname}
                                                </strong>{' '}
                                                ({guest?.email})?
                                            </>
                                        ),
                                        confirm: () => {
                                            handlePwreset(guest?.email, guest?.company_id);
                                        }
                                    });
                                }}
                                size="small"
                                color="primary"
                                fullWidth
                                startIcon={<Email />}
                            >
                                Send Password Reset Email
                            </Button>
                        </Grid>
                    </Grid>
                )}
                {showNotes && isAgent && guest?.internal_notes !== null && (
                    <Grid item xs={12}>
                        <h5>Internal notes</h5>
                        <h4>{guest?.internal_notes}</h4>
                    </Grid>
                )}
                {isAgent && (
                    <>
                        <Grid item xs={12}>
                            <h5>Created by</h5>
                            <h4>
                                {guest?.created_by ? `${guest?.created_by}` : `Self registration`}
                            </h4>
                            <h4>{moment(guest?.created_at).format('ddd. MMM DD, YYYY')}</h4>
                        </Grid>
                        <Grid item xs={12}>
                            <h5>Source</h5>
                            <h4>{guest?.source || '-'}</h4>
                        </Grid>
                    </>
                )}
            </Grid>

            {!isAgent && (
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                                try {
                                    window.cookiedone.showCookieSettings();
                                } catch (err) {
                                    //
                                }
                            }}
                            fullWidth
                        >
                            Show cookie settings
                        </Button>
                    </Grid>
                </Grid>
            )}
        </CardContent>
    );
}
